import {
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Plus, X } from "@phosphor-icons/react";
import { useChurchStore } from "features/churches";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateTask,
  useDeleteTask,
  useTasks,
  useUpdateTask,
  useUpdateTaskStatus
} from "../api";

export const TasksPanel = () => {
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const [showInput, setShowInput] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");

  const { t } = useTranslation();

  const { data: tasks, isLoading } = useTasks({
    churchId: selectedChurch?.id
  });

  const { mutate: createTask } = useCreateTask();
  const { mutate: updateTaskStatus } = useUpdateTaskStatus();
  const { mutate: updateTask } = useUpdateTask();
  const { mutate: deleteTask } = useDeleteTask();

  const handleAddTaskClick = () => {
    setShowInput(true);
  };

  const handleCreateTask = () => {
    if (newTaskTitle.trim()) {
      createTask(
        {
          churchId: selectedChurch?.id,
          task: {
            title: newTaskTitle.trim(),
            description: ""
          }
        },
        {
          onSuccess: () => {
            setNewTaskTitle("");
            setShowInput(false);
          }
        }
      );
    } else {
      setShowInput(false);
    }
  };

  return (
    <Stack
      sx={{
        border: "1px solid rgba(255, 255, 255, 0.5)",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(8px)",
        p: 2,
        borderRadius: 6
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ mb: 1, color: "#fff", fontWeight: 700 }}
      >
        {t("quickboard.todayTasks")}
      </Typography>
      <Stack
        sx={{
          maxHeight: "200px",
          overflowY: "auto",
          overflowX: "hidden",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px"
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255, 255, 255, 0.1)"
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.5)",
            borderRadius: "3px"
          }
        }}
      >
        {tasks?.map(task => (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            key={task.id}
            onMouseEnter={() => setHoveredTaskId(task.id)}
            onMouseLeave={() => setHoveredTaskId(null)}
            sx={{ position: "relative" }}
          >
            <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
              <Checkbox
                checked={task.status === "completed"}
                sx={{
                  color: "rgba(255, 255, 255, 0.5)",
                  "&.Mui-checked": {
                    color: "success.main"
                  }
                }}
                onChange={() =>
                  updateTaskStatus({
                    churchId: selectedChurch?.id,
                    taskId: task.id,
                    status:
                      task.status === "completed" ? "pending" : "completed"
                  })
                }
              />
              {editingTaskId === task.id ? (
                <TextField
                  autoFocus
                  fullWidth
                  value={editingTitle}
                  onChange={e => setEditingTitle(e.target.value)}
                  onBlur={() => {
                    if (editingTitle.trim() && editingTitle !== task.title) {
                      updateTask({
                        churchId: selectedChurch?.id,
                        taskId: task.id,
                        task: { title: editingTitle.trim() }
                      });
                    }
                    setEditingTaskId(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      if (editingTitle.trim() && editingTitle !== task.title) {
                        updateTask({
                          churchId: selectedChurch?.id,
                          taskId: task.id,
                          task: { title: editingTitle.trim() }
                        });
                      }
                      setEditingTaskId(null);
                    }
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "#fff",
                      "&:before, &:after": {
                        display: "none"
                      }
                    },
                    "& .MuiInputBase-input": {
                      "&::placeholder": {
                        color: "rgba(255, 255, 255, 0.5)",
                        opacity: 1
                      }
                    }
                  }}
                  variant="standard"
                />
              ) : (
                <Typography
                  variant="subtitle2"
                  onClick={() => {
                    setEditingTaskId(task.id);
                    setEditingTitle(task.title);
                  }}
                  sx={{
                    color: "#fff",
                    textDecoration:
                      task.status === "completed" ? "line-through" : "none",
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline"
                    }
                  }}
                >
                  {task.title}
                </Typography>
              )}
            </Stack>
            {hoveredTaskId === task.id && !editingTaskId && (
              <IconButton
                onClick={() =>
                  deleteTask({ churchId: selectedChurch?.id, taskId: task.id })
                }
              >
                <X size={16} color="#fff" />
              </IconButton>
            )}
          </Stack>
        ))}

        {tasks?.length === 0 && !showInput && (
          <Typography variant="subtitle2" sx={{ color: "#fff" }}>
            {t("quickboard.noTasksToday")}
          </Typography>
        )}

        {showInput && (
          <Stack direction="row" alignItems="center">
            <Checkbox
              disabled
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                "&.Mui-disabled": {
                  color: "rgba(255, 255, 255, 0.5)"
                }
              }}
            />
            <TextField
              autoFocus
              fullWidth
              value={newTaskTitle}
              onChange={e => setNewTaskTitle(e.target.value)}
              onBlur={handleCreateTask}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleCreateTask();
                }
              }}
              sx={{
                "& .MuiInputBase-root": {
                  color: "#fff",
                  "&:before, &:after": {
                    display: "none"
                  }
                },
                "& .MuiInputBase-input": {
                  "&::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1
                  }
                }
              }}
              variant="standard"
            />
          </Stack>
        )}
      </Stack>

      {isLoading && (
        <Stack alignItems="center" justifyContent="center">
          <CircularProgress size="24px" sx={{ color: "#fff" }} />
        </Stack>
      )}

      {!showInput && (
        <Stack direction="row" alignItems="center" justifyContent="flex-start">
          <Button
            variant="text"
            startIcon={<Plus size={16} />}
            sx={{
              color: "#fff"
            }}
            onClick={handleAddTaskClick}
          >
            {t("quickboard.addTask")}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
